import React, { useEffect, useState } from "react";
import { Field, Form, FormikProvider, useFormik } from "formik";
import {
    InputAdornment,
    makeStyles,
    TextField,
    useMediaQuery,
    useTheme,
} from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import * as Yup from "yup";
import Axios from "axios";
import { NODE_API_URL, LOGO_BASE_URL, LOGIN } from "../../../../env_config";
import useValidatedImage from "../../../../hooks/useValidatedImage";
import { TbLockPassword } from "react-icons/tb";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { actions } from "../../../../app/modules/Auth/_redux/authRedux";
import { SetCookies } from "../../../../_metronic/_helpers/Cookies";
import { useDomain } from "../../../../contexts/DomainContext";
import { useLanguage } from "../../../../contexts/LanguageContext";
import FullPageLoader from "../../../../_metronic/_partials/FullPageLoader";

const initialValues = {
    password: "",
    confirmPassword: "",
};

const AcceptInvitation = () => {
    const language = useLanguage();
    const { origin } = useDomain();

    const validationSchemaAcceptInvite = Yup.object().shape({
        password: Yup.string()
            .required(language.CCMODE_AUTH_PASSWORD_IS_REQUIRED)
            .matches(
                /^(?=.*[a-z])/,
                language.CCMODE_AUTH_PASSWORD_LOWERCASE
            )
            .matches(
                /^(?=.*[A-Z])/,
                language.CCMODE_AUTH_PASSWORD_UPPERCASE
            )
            .matches(/^(?=.*[0-9])/, language.CCMODE_AUTH_PASSWORD_NUMBER)
            .matches(
                /^(?=.*[!@#$%^&*])/,
                language.CCMODE_AUTH_PASSWORD_SPECIAL_CHAR
            )
            .min(8, language.CCMODE_AUTH_PASSWORD_MIN_8),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password"), null], language.CCMODE_AUTH_PASSWORD_MUST_MATCH)
            .required(language.CCMODE_AUTH_CONFIRM_PASSWORD_IS_REQUIRED),
    });

    const theme = useTheme();
    const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));

    const dispatch = useDispatch();
    const history = useHistory();
    const { token } = useParams();

    const [settings, setSettings] = useState(null);
    const [loader, setLoader] = useState(false);

    let defaultImageUrl = require("./Images/ccmode_login_bg2.png");

    useEffect(() => {
        if (token) {
            console.log(token);
            async function loadCCModeSetting() {
                setLoader(true);
                try {
                    const res = await Axios.post(
                        `${NODE_API_URL}/ccmode/get-settings-by-invite-token`,
                        {
                            inviteToken: token,
                        },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                "Access-Control-Allow-Origin": "*",
                            },
                        }
                    );

                    // backend-level failure
                    if (res.data.success === false) {
                        return;
                    }

                    setSettings(res.data.data);
                } catch (err) {
                    console.log(err);
                } finally {
                    setLoader(false);
                }
            }

            loadCCModeSetting();
        }
    }, [token]);

    const remoteLogoImageUrl = `${LOGO_BASE_URL}/${encodeURIComponent(
        settings?.Logo || ""
    )}`;
    const logoUrl = useValidatedImage({ remoteUrl: remoteLogoImageUrl });

    const remoteBgImageUrl = `${LOGO_BASE_URL}/${encodeURIComponent(
        settings?.background_image || ""
    )}`;
    const bgUrl = useValidatedImage({ remoteUrl: remoteBgImageUrl });

    const useStyles = makeStyles((theme) => ({
        inviteContainer: {
            width: "100vw",
            height: "100vh",
            display: "flex",

            [theme.breakpoints.down("xs")]: {
                flexDirection: "column",
            },
        },
        bg: {
            backgroundImage: `url('${
                bgUrl.isValid ? bgUrl.imageUrl : defaultImageUrl
            }')`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            height: "100%",
            backgroundPosition: "center center",
            backgroundColor: settings?.background_color || "#EEF0F8",
        },
        inviteForm: {
            backgroundColor: settings?.nav_bar_color || "#F4F6FC",

            [theme.breakpoints.down("xs")]: {
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                width: "90%",
                height: "65%",
                borderRadius: "15px",
                backgroundColor:
                    settings?.nav_bar_color || "rgba(244, 246, 252, 0.8)",
            },
        },
        textField: {
            "& .MuiInputBase-input": {
                color: `#000 !important`,
            },
            backgroundColor: "#fff !important",
            "& .MuiInput-underline:before, & .MuiInput-underline:active:not(.Mui-focused):before, & .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInput-underline:after": {
                borderBottom: "none !important",
            },
            "& input:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 1000px #ffffff inset !important",
                WebkitTextFillColor: `#000 !important`,
            },
            [theme.breakpoints.down("xs")]: {
                "& .MuiInputBase-input": {
                    fontSize: "16px !important",
                },
            },
        },
        newLoginBtn: {
            width: "10rem",
            height: "auto",
            position: "relative",
            display: "inline-block",
            cursor: "pointer",
            outline: "none",
            border: 0,
            verticalAlign: "middle",
            textDecoration: "none",
            background: "transparent",
            padding: 0,
            fontSize: "inherit",
            fontFamily: "inherit",

            "& .circle": {
                transition: "all 0.45s cubic-bezier(0.65, 0, 0.076, 1)",
                position: "relative",
                display: "block",
                margin: 0,
                width: "3rem",
                height: "3rem",
                background:
                    settings?.background_color ||
                    `linear-gradient(90deg, rgba(0,164,245,1) 0%, rgba(0,76,200,1) 100%) !important`,
                borderRadius: "1.625rem",
            },

            "& .circle .icon": {
                transition: "all 0.45s cubic-bezier(0.65, 0, 0.076, 1)",
                position: "absolute",
                top: 0,
                bottom: 0,
                margin: "auto",
                background: "#fff",
            },

            "& .circle .icon.arrow": {
                left: "0.625rem",
                width: "1.125rem",
                height: "0.125rem",
                background: "none",
            },

            "& .circle .icon.arrow::before": {
                content: '""',
                position: "absolute",
                top: "-0.25rem",
                right: "0.0625rem",
                width: "0.625rem",
                height: "0.625rem",
                borderTop: `0.125rem solid ${settings?.nav_bar_font || "#fff"}`,
                borderRight: `0.125rem solid ${settings?.nav_bar_font ||
                    "#fff"}`,
                transform: "rotate(45deg)",
            },

            "& .button_text": {
                transition: "all 0.45s cubic-bezier(0.65, 0, 0.076, 1)",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                padding: "0.75rem 0",
                margin: "0 0 0 1rem",
                fontWeight: 500,
                lineHeight: 1.6,
                textAlign: "center",
                textTransform: "uppercase",
                color: settings?.nav_bar_font || "#616161",
            },

            "&:hover .circle": {
                width: "100%",
            },

            "&:hover .circle .icon.arrow": {
                background: settings?.nav_bar_font || "#fff",
                transform: "translate(1rem, 0)",
            },

            "&:hover .button_text": {
                color: settings?.nav_bar_font || "#fff",
            },

            [theme.breakpoints.down("xs")]: {
                "& .circle": {
                    width: "100%",
                },
                "& .circle .icon.arrow": {
                    background: settings?.nav_bar_font || "#fff",
                    transform: "translate(1rem, 0)",
                },

                "& .button_text": {
                    color: settings?.nav_bar_font || "#fff",
                },
            },
        },
    }));

    const classes = useStyles();

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchemaAcceptInvite,
        onSubmit: (values, { setStatus, setSubmitting }) => {
            Axios({
                method: "POST",
                url: `${NODE_API_URL}/ccmode/ccmode-invite/accept`,
                headers: {
                    "Content-Type": "application/json",
                },
                data: {
                    token,
                    ...values,
                },
            })
                .then((res) => {
                    setSubmitting(false);
                    toast.dismiss();
                    toast.success(
                        language.CCMODE_AUTH_ACCOUNT_ACTIVE
                    );
                    Axios({
                        method: "POST",
                        url: LOGIN,
                        headers: {
                            "Access-Control-Allow-Origin": "*",
                            "Content-Type": "application/json",
                        },
                        data: {
                            mail: settings?.email,
                            password: values.password,
                            client: origin,
                        },
                    })
                        .then(function(res) {
                            if (res && res.data) {
                                const user = JSON.stringify(
                                    res.data.Customer[0]
                                );
                                const accessToken = res.data.accessToken;
                                localStorage.setItem("userData", user);
                                localStorage.setItem("C_id", -1);
                                SetCookies("resData", user);
                                localStorage.setItem(
                                    "accessToken",
                                    res.data.accessToken
                                );
                                dispatch(actions.login(accessToken));
                                dispatch(actions.setUser(user));
                                history.push("/searchdiamond");
                            }
                        })
                        .catch((res) => {
                            history.push("/auth");
                        });
                    // history.push("/auth");
                })
                .catch((error) => {
                    if (error?.response?.data?.message) {
                        setStatus(error?.response?.data?.message);
                    }
                });
        },
    });

    const getInputClasses = (fieldname) => {
        if (formik.touched[fieldname] && formik.errors[fieldname]) {
            return "is-invalid";
        }

        if (formik.touched[fieldname] && !formik.errors[fieldname]) {
            return "is-valid";
        }

        return "";
    };

    if (loader) {
        return <FullPageLoader open={loader} />;
    }

    return (
        <div
            className={`${classes.inviteContainer} col-12 px-0`}
            id="kt_login_signin_form"
        >
            <div
                className={`${classes.bg} ${isXsDown ? "col-12" : "col-8"}`}
            ></div>
            <div
                className={`${classes.inviteForm} ${
                    isXsDown ? "col-12" : "col-4"
                } d-flex flex-center`}
            >
                <div className={`${isXsDown ? "col-12" : "col-10"}`}>
                    {logoUrl.isValid ? (
                        <div className="text-center mb-6">
                            <img
                                alt="Logo"
                                src={logoUrl.imageUrl}
                                height="76px"
                                style={{
                                    maxWidth: "200px",
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    ) : (
                        ""
                    )}
                    <FormikProvider value={formik}>
                        <Form
                            onSubmit={formik.handleSubmit}
                            className="form fv-plugins-bootstrap fv-plugins-framework"
                            autoComplete="off"
                        >
                            {formik.status ? (
                                <div className="mb-10 login-alert alert alert-custom alert-light-danger alert-dismissible py-3">
                                    <div className="alert-text font-weight-bold">
                                        {formik.status}
                                    </div>
                                </div>
                            ) : (
                                <div className=""></div>
                            )}
                            <div className="row">
                                <div className="col-12">
                                    <div className="mb-5 login fv-plugins-icon-container">
                                        <Field
                                            className={`form-control form-control-solid h-auto py-3 px-6 ${getInputClasses(
                                                "password"
                                            )} ${classes.textField}`}
                                            type="password"
                                            name="password"
                                            placeholder={language.CCMODE_AUTH_ENTER_PASSWORD}
                                            as={TextField}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        color="#fff"
                                                        style={{
                                                            padding: "8px",
                                                            margin: "0",
                                                        }}
                                                    >
                                                        <TbLockPassword
                                                            color="#0261CC"
                                                            size={18}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {formik.errors.password ? (
                                            <div className="invalid-feedback">
                                                {formik.errors.password}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="mb-5 login fv-plugins-icon-container">
                                        <Field
                                            className={`form-control form-control-solid h-auto py-3 px-6 ${getInputClasses(
                                                "confirmPassword"
                                            )} ${classes.textField}`}
                                            type="password"
                                            name="confirmPassword"
                                            placeholder={language.CCMODE_AUTH_CONFIRM_PASSWORD}
                                            as={TextField}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        color="#fff"
                                                        style={{
                                                            padding: "8px",
                                                            margin: "0",
                                                        }}
                                                    >
                                                        <TbLockPassword
                                                            color="#0261CC"
                                                            size={18}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {formik.errors.confirmPassword ? (
                                            <div className="invalid-feedback">
                                                {formik.errors.confirmPassword}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="col-12 mt-2">
                                    <div className="text-center">
                                        <button
                                            className={`${classes.newLoginBtn} submit`}
                                            type="submit"
                                            disabled={formik.isSubmitting}
                                            style={{ width: "180px" }}
                                        >
                                            <span
                                                className={`circle`}
                                                aria-hidden="true"
                                            >
                                                <span
                                                    className={`icon arrow`}
                                                ></span>
                                            </span>
                                            <span
                                                className={`button_text ml-10`}
                                            >
                                                {language.CCMODE_AUTH_CREATE_ACCOUNT}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </FormikProvider>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;
